import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { DataTable } from '@components/DataTable';
import { DataTableColumn } from '@components/DataTable/types';
import {
    GET_FEATURE_CHANGE_LOGS,
    GET_FEATURES_UPERCUT,
    GET_PRO_SUBSCRIPTION_PLANS,
} from '@gql/subscriptions';
import { usePageSize } from '@hooks/usePageSize';
import {
    Autocomplete,
    Box,
    Divider,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useParams } from 'react-router-dom';
import BooleanFeature from './components/BooleanFeature';
import CreditsFeature from './components/CreditsFeature';
import DurationFeature from './components/DurationFeature';
import LimitFeature from './components/LimitFeature';
import { ChangeLogEntry, Feature, SubscriptionPlan } from './types';
import { renderFeatureName } from './components/renderFeatureName';

interface FeatureTypeOption {
    value: string;
    label: string;
}

const FEATURE_TYPES: FeatureTypeOption[] = [
    { value: 'boolean', label: 'Activation' },
    { value: 'credits', label: 'Crédits' },
    { value: 'duration', label: 'Durée' },
    { value: 'limit', label: 'Limite' },
];

const isEditableStatus = (status: string) => ['active', 'trialing'].includes(status);

const formatConfigValue = (config: any) => {
    if (!config) return '-';
    if (config.type === 'boolean') return config.enabled ? 'Activé' : 'Désactivé';
    if (config.type === 'credits') return `${config.used ?? 0} / ${config.maximum === -1 ? "∞" : config.maximum ?? "-"}`;
    if (config.type === 'duration') return `${config.duration ?? 0} ${config.unit ?? ''}`;
    if (config.type === 'limit') return `${config.limit ?? 0}`;
    return JSON.stringify(config);
};

const formatDate = (date: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const CompanyFeatures = () => {
    const { id } = useParams<{ id: string }>();

    const [selectedPlanId, setSelectedPlanId] = useState<string>('');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
    const [logPage, setLogPage] = useState(0);
    const { pageSize: logRowsPerPage, storePageSize } = usePageSize({ storageKey: 'company-features-log-page-size' });

    const selectedTypeOptions = useMemo(
        () => FEATURE_TYPES.filter(({ value }) => selectedTypes.includes(value)),
        [selectedTypes],
    );

    // Fetch subscription plans
    const { data: proData } = useQuery(GET_PRO_SUBSCRIPTION_PLANS, {
        variables: { proId: id },
        skip: !id,
    });

    const plans: SubscriptionPlan[] = useMemo(
        () => proData?.getPro?.userSubscriptionPlans ?? [],
        [proData],
    );

    const selectedPlan = useMemo(
        () => plans.find((p) => p._id === selectedPlanId),
        [plans, selectedPlanId],
    );

    const isEditable = selectedPlan ? isEditableStatus(selectedPlan.status) : false;

    // Auto-select first active/trialing plan
    useEffect(() => {
        if (plans.length > 0 && !selectedPlanId) {
            const activePlan = plans.find((p) => isEditableStatus(p.status));
            setSelectedPlanId(activePlan?._id ?? plans[0]._id ?? '');
        }
    }, [plans, selectedPlanId]);

    // Fetch features
    const { data: featuresData, loading: loadingFeatures } = useQuery(
        GET_FEATURES_UPERCUT,
        {
            variables: {
                filter: {
                    proId: id,
                    planName: selectedPlan?.name,
                },
            },
            skip: !id || !selectedPlanId,
            fetchPolicy: 'network-only',
        },
    );

    const features: Feature[] = useMemo(
        () =>
            [...(featuresData?.getFeaturesUpercut ?? [])].sort((a, b) =>
                (a.configuration?.type ?? '').localeCompare(b.configuration?.type ?? ''),
            ).filter(item => {
                return ![
                    "premium_jobs_credits",
                    "classic_jobs_credits",
                ].includes(item.key) && item.configuration.type !== "boolean" // Filter out deprecated features
            }),
        [featuresData],
    );

    const filteredFeatures = useMemo(() => {
        const q = searchQuery.toLowerCase();
        return features.filter((f) => {
            const matchesSearch =
                !q || f.name.toLowerCase().includes(q) || f.key.toLowerCase().includes(q);
            const matchesType =
                selectedTypes.length === 0 || selectedTypes.includes(f.configuration?.type);
            return matchesSearch && matchesType;
        });
    }, [features, searchQuery, selectedTypes]);

    // Change logs
    const featureIds = useMemo(() => features.map((f) => f._id), [features]);

    const {
        data: changeLogsData,
        loading: loadingChangeLogs,
        refetch: refetchChangeLogs,
    } = useQuery(GET_FEATURE_CHANGE_LOGS, {
        variables: {
            featureIds,
            page: logPage + 1,
            limit: logRowsPerPage,
        },
        skip: featureIds.length === 0,
    });

    const changeLogs: ChangeLogEntry[] = useMemo(
        () => changeLogsData?.getFeatureChangeLogsUpercut?.docs ?? [],
        [changeLogsData],
    );

    const changeLogsMeta = useMemo(
        () => changeLogsData?.getFeatureChangeLogsUpercut?.meta ?? { totalDocs: 0 },
        [changeLogsData],
    );

    const changeLogColumns = useMemo<DataTableColumn<ChangeLogEntry>[]>(
        () => [
            {
                id: 'createdAt',
                label: 'Date',
                width: 180,
                renderCell: ({ row }) => formatDate(row.createdAt),
            },
            {
                id: 'feature',
                label: 'Fonctionnalité',
                width: 300,
                renderCell: ({ row }) => renderFeatureName(row.feature?.name || row.featureId || '-'),
            },
            {
                id: 'before',
                label: 'Avant',
                width: 50,
                renderCell: ({ row }) => formatConfigValue(row.configurationBefore),
            },
            {
                id: 'after',
                label: 'Après',
                width: 50,
                renderCell: ({ row }) => formatConfigValue(row.configurationAfter),
            },
            {
                id: 'reason',
                label: 'Raison',
                renderCell: ({ row }) => row.reason || '-',
            },
            // {
            //     id: 'createdBy',
            //     label: 'Modifié par',
            //     minWidth: 150,
            //     renderCell: ({ row }) => {
            //         if (row.createdByUser) {
            //             const { firstname, lastname, email } = row.createdByUser;
            //             if (firstname || lastname) return `${firstname ?? ''} ${lastname ?? ''}`.trim();
            //             return email || row.createdBy || '-';
            //         }
            //         return row.createdBy || '-';
            //     },
            // },
        ],
        [],
    );

    // Reset log page and filters when plan changes
    useEffect(() => {
        setLogPage(0);
        setSearchQuery('');
        setSelectedTypes([]);
    }, [selectedPlanId]);

    const renderFeature = (feature: Feature) => {
        const config = feature.configuration;

        if (config.type === 'boolean') {
            return (
                <BooleanFeature
                    key={feature._id}
                    feature={feature}
                    isEditable={isEditable}
                    onChangeLogRefresh={refetchChangeLogs}
                />
            );
        }

        if (config.type === 'credits') {
            return (
                <CreditsFeature
                    key={feature._id}
                    feature={feature}
                    isEditable={isEditable}
                    onChangeLogRefresh={refetchChangeLogs}
                />
            );
        }

        if (config.type === 'duration') {
            return (
                <DurationFeature
                    key={feature._id}
                    feature={feature}
                    isEditable={isEditable}
                    onChangeLogRefresh={refetchChangeLogs}
                />
            );
        }

        if (config.type === 'limit') {
            return (
                <LimitFeature
                    key={feature._id}
                    feature={feature}
                    isEditable={isEditable}
                    onChangeLogRefresh={refetchChangeLogs}
                />
            );
        }

        return null;
    };

    return (
        <Box flex={1} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
                Fonctionnalités
            </Typography>
            {/* Search & Filter */}
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 4, flexWrap: 'wrap', gap: 1 }} justifyContent="space-between">
                {/* Plan Selector */}
                <TextField
                    size="small"
                    placeholder="Rechercher par nom ou clé…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ maxWidth: 400, width: "100%" }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
                <Stack direction="row" alignItems="center" sx={{ gap: 1, flex: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <FormControl sx={{ maxWidth: 400, width: "100%" }} size="small">
                        <InputLabel shrink>Plan d'abonnement</InputLabel>
                        <Select
                            value={selectedPlanId}
                            sx={{ maxWidth: 400, width: "100%" }}
                            displayEmpty
                            onChange={(e) => setSelectedPlanId(e.target.value)}
                            renderValue={(selected) => {
                                const selectedPlan = plans.find((p) => p._id === selected);
                                if (selected.length === 0) {
                                    return <span style={{ color: '#aaa' }}>Sélectionner un plan</span>;
                                }

                                return `${selectedPlan?.name} (${selectedPlan?.status})`;
                            }}
                        >
                            {plans.map((plan) => (
                                <MenuItem key={plan._id} value={plan._id}>
                                    {plan.name} ({plan.status})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Autocomplete
                        multiple
                        options={FEATURE_TYPES}
                        value={selectedTypeOptions}
                        getOptionLabel={(option) => option.label}
                        onChange={(_, value) => setSelectedTypes(value.map((option) => option.value))}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        renderInput={(params) => (
                            <TextField {...params} label="Types" placeholder="Tous les types" InputLabelProps={{ shrink: true }} />
                        )}
                        sx={{ maxWidth: 400, width: "100%" }}
                    />
                </Stack>
            </Stack>

            {!isEditable && selectedPlan && (
                <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                    Ce plan n'est pas actif. Les fonctionnalités sont en lecture seule.
                </Typography>
            )}


            {/* Features */}
            <Box sx={{ mb: 4 }}>
                <Grid container spacing={2}>
                    {filteredFeatures.map((item) => {
                        const rendered = renderFeature(item);
                        return rendered ? <Grid key={item._id} item xs={12} sm={6}>{rendered}</Grid> : null;
                    })}
                </Grid>

                {filteredFeatures.length === 0 && !loadingFeatures && (
                    <Typography variant="body2" color="text.secondary">
                        Aucune fonctionnalité trouvée pour ce plan.
                    </Typography>
                )}
            </Box>

            {/* Change Logs */}
            <Divider sx={{ mb: 3 }} />

            <Typography variant="h6" sx={{ mb: 2 }}>
                Historique des modifications
            </Typography>

            <Box sx={{ flex: 1, minHeight: 0 }}>
                <DataTable
                    stickyHeader
                    rows={changeLogs}
                    columns={changeLogColumns}
                    loading={loadingChangeLogs}
                    pagination={{
                        currentPage: logPage + 1,
                        pageSize: logRowsPerPage,
                        totalRows: changeLogsMeta.totalDocs,
                        onPageChange: (newPage) => setLogPage(newPage - 1),
                        onPageSizeChange: (newSize) => {
                            storePageSize(newSize);
                            setLogPage(0);
                        },
                    }}
                />
            </Box>
        </Box>
    );
};

export default CompanyFeatures;
